import { ApiCostsDataResponse, ApiProductivityResponse, ApiResponse, PeriodData } from '../types/api';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export class ExportService {
  
  async exportToExcelV2(headers: string[], data: string[][], fileName: string): Promise<void> {
    try {
      const excelData = this.prepareExcelDataV2(headers, data);
      
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);
      
      XLSX.utils.book_append_sheet(wb, ws, 'Reporte');
      
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      saveAs(blob, `${fileName}_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      throw new Error('No se pudo exportar a Excel');
    }
  }
  
  async exportToExcel(data: ApiResponse): Promise<void> {
    try {
      const excelData = this.prepareExcelData(data);
      
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);
      
      XLSX.utils.book_append_sheet(wb, ws, 'Datos Muros');
      
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      saveAs(blob, `muros_dashboard_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      throw new Error('No se pudo exportar a Excel');
    }
  }

  private prepareExcelData(data: ApiResponse): any[] {
    const excelData = [];
    
    excelData.push({
      'Periodo': 'Periodo',
      'Programado': 'Programado',
      'Ejecutado': 'Ejecutado',
      'Proyectado': 'Proyectado'
    });
    
    data.meteredPerPeriodLists.forEach((period: PeriodData) => {
      excelData.push({
        'Periodo': period.Description.Value,
        'Programado': period.Programmed?.Value || '0',
        'Ejecutado': period.Executed?.Value || '0',
        'Proyectado': period.Projected?.Value || '0'
      });
    });
    
    return excelData;
  }

  private prepareExcelDataV2(headers: string[], data: string[][]): any[] {
    const excelData: any = [];
    data.forEach((row: string[]) => {
      const rowObject = Object.fromEntries(
        headers.map((header, index) => [header, row[index]])
      );
      excelData.push(rowObject);
    });
    console.log('excelData', excelData)
    
    return excelData;
  }

  async exportToPDF(
    data: ApiResponse, 
    element: HTMLElement
  ): Promise<void> {
    try {
      const pdf = new jsPDF('landscape', 'mm', 'a4');

      let yPosition = 10;
      pdf.setFontSize(14);
      pdf.text(`${data.Description} [${data.UnitMeasurement}]`, 20, yPosition);
      pdf.setFontSize(10);
      
      yPosition += 10;
      pdf.text(`Inicio: ${data.strStart}`, 20, yPosition);
      pdf.text(`Fin: ${data.strEnd}`, 100, yPosition);
      pdf.text(`Duración: ${data.Duration} días`, 180, yPosition);
      
      yPosition += 10;
      // pdf.text(`Cant. Total: ${data.Metered}`, 20, yPosition);
      pdf.text(`Proyectado: ${Number(data.ProjectedTotal)}`, 20, yPosition);
      pdf.text(`Por ejecutar: ${Number(data.RestTotal)}`, 100, yPosition);
      pdf.text(`Ejecutado: ${Number(data.ExecutedTotal)}`, 180, yPosition);
      
      // yPosition += 10;
      
      yPosition += 10;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false
      });
      
      const imgData = canvas.toDataURL('image/png');
      const imgWidth = 250;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      if (yPosition + imgHeight > 190) {
        pdf.addPage();
        yPosition = 20;
      }
      pdf.addImage(imgData, 'PNG', 20, yPosition, imgWidth, imgHeight);
      
      pdf.save(`${data.Description}_progreso_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      throw new Error('No se pudo exportar a PDF');
    }
  }

  async exportCostsToPDF(
    data: ApiCostsDataResponse, 
    element: HTMLElement
  ): Promise<void> {
    try {
      const pdf = new jsPDF('landscape', 'mm', 'a4');

      let yPosition = 10;
      pdf.setFontSize(14);
      pdf.text(`${data.Name} [${data.UnitMeasurement}]`, 20, yPosition);
      pdf.setFontSize(10);
      
      yPosition += 10;
      pdf.text(`Inicio: ${data.StartDate}`, 20, yPosition);
      pdf.text(`Fin: ${data.EndDate}`, 80, yPosition);
      pdf.text(`Duración: ${data.Duration} días`, 140, yPosition);
      pdf.text(`Total: ${Number(data.Metered)}`, 200, yPosition);
      
      yPosition += 10;
      // pdf.text(`Cant. Total: ${data.Metered}`, 20, yPosition);
      pdf.text(`Ejecutado: ${Number(data.MeteredExcuted)}`, 20, yPosition);
      pdf.text(`Por ejecutar: ${Number(data.MeteredBalance)}`, 80, yPosition);
      pdf.text(`Presupuestado: ${Number(data.BudgetCost)}`, 140, yPosition);
      pdf.text(`Planeado: ${Number(data.PlannedCost)}`, 200, yPosition);      
      // yPosition += 10;
      
      yPosition += 10;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false
      });
      
      const imgData = canvas.toDataURL('image/png');
      const imgWidth = 250;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      if (yPosition + imgHeight > 190) {
        pdf.addPage();
        yPosition = 20;
      }
      pdf.addImage(imgData, 'PNG', 20, yPosition, imgWidth, imgHeight);
      
      pdf.save(`${data.Name}_costos_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      throw new Error('No se pudo exportar a PDF');
    }
  }

  async exportProductivityToPDF(
    data: ApiProductivityResponse, 
    element: HTMLElement
  ): Promise<void> {
    try {
      const pdf = new jsPDF('landscape', 'mm', 'a4');
      
      let yPosition = 10;
      pdf.setFontSize(14);
      pdf.text(`${data.Name} [${data.UnitMeasurement}]`, 20, yPosition);
      pdf.setFontSize(10);
      
      yPosition += 10;
      pdf.text(`Productividad: ${Number(data.Productividad)}`, 20, yPosition);
      pdf.text(`Productividad Esperada: ${Number(data.ProductividadEsperada)}`, 80, yPosition);  
      // yPosition += 10;
      
      yPosition += 10;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false
      });
      
      const imgData = canvas.toDataURL('image/png');
      const imgWidth = 250;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      if (yPosition + imgHeight > 190) {
        pdf.addPage();
        yPosition = 20;
      }
      pdf.addImage(imgData, 'PNG', 20, yPosition, imgWidth, imgHeight);
      
      pdf.save(`${data.Name}_productividad_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      throw new Error('No se pudo exportar a PDF');
    }
  }

  async exportChartAsImage(elementId: string, filename: string = 'chart'): Promise<void> {
    try {
      const element = document.getElementById(elementId);
      if (!element) {
        throw new Error('Elemento no encontrado');
      }

      const canvas = await html2canvas(element);
      canvas.toBlob((blob) => {
        if (blob) {
          saveAs(blob, `${filename}_${new Date().toISOString().split('T')[0]}.png`);
        }
      });
    } catch (error) {
      console.error('Error exporting chart as image:', error);
      throw new Error('No se pudo exportar el gráfico como imagen');
    }
  }
}